import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';

export interface TaskDetail {
  id: string;
  title: string;
  description: string | null;
  project_id: string;
}

export interface TaskNoteDetail {
  id: string;
  task_id: string;
  task_title: string;
  content: string;
  created_by: string | null;
}

export interface ChatMessageDetail {
  id: string;
  content: string | null;
  sender_name: string | null;
}

export interface CalendarEventDetail {
  id: string;
  title: string;
  start_date: string;
}

export interface BookingDetail {
  id: string;
  description: string;
  amount: number;
  type: string;
}

export interface CampaignDetail {
  id: string;
  name: string;
  campaign_type: string;
}

export interface InsuranceDetail {
  id: string;
  insurance_type: string;
  provider: string;
}

export interface ContactDetail {
  id: string;
  first_name: string | null;
  last_name: string | null;
  company_name: string | null;
}

export interface LoginSummary {
  tasks: TaskDetail[];
  taskNotes: TaskNoteDetail[];
  chatMessages: ChatMessageDetail[];
  calendarEvents: CalendarEventDetail[];
  projects: { id: string; name: string }[];
  files: { id: string; name: string }[];
  bookings: BookingDetail[];
  campaigns: CampaignDetail[];
  insurances: InsuranceDetail[];
  contacts: ContactDetail[];
}

const SESSION_KEY = 'login_summary_shown';

export const useLoginSummary = () => {
  const [summary, setSummary] = useState<LoginSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [shouldShow, setShouldShow] = useState(false);

  const fetchSummary = useCallback(async () => {
    try {
      // Check if already shown in this session
      if (sessionStorage.getItem(SESSION_KEY)) {
        setLoading(false);
        setShouldShow(false);
        return;
      }

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setLoading(false);
        return;
      }

      // Get last seen timestamp
      const { data: lastSeenData } = await supabase
        .from('user_last_seen')
        .select('last_seen_at')
        .eq('user_id', user.id)
        .single();

      const lastSeen = lastSeenData?.last_seen_at || '1970-01-01T00:00:00Z';

      // Fetch all new items in parallel
      const [
        tasksRes,
        taskNotesRes,
        chatRes,
        calendarRes,
        projectsRes,
        filesRes,
        bookingsRes,
        campaignsRes,
        insurancesRes,
        contactsRes
      ] = await Promise.all([
        // New tasks with details
        supabase
          .from('tasks')
          .select('id, title, description, project_id')
          .gt('created_at', lastSeen)
          .neq('created_by', user.id)
          .limit(10),
        
        // New task notes with task title
        supabase
          .from('task_notes')
          .select('id, task_id, content, created_by, tasks!inner(title)')
          .gt('created_at', lastSeen)
          .neq('created_by', user.id)
          .limit(10),
        
        // New chat messages
        supabase
          .from('chat_messages')
          .select('id, content, sender_id, profiles!chat_messages_sender_id_fkey(full_name)')
          .is('recipient_id', null)
          .gt('created_at', lastSeen)
          .neq('sender_id', user.id)
          .limit(10),
        
        // New calendar events
        supabase
          .from('calendar_events')
          .select('id, title, start_date')
          .gt('created_at', lastSeen)
          .neq('created_by', user.id)
          .limit(10),
        
        // New projects
        supabase
          .from('projects')
          .select('id, name')
          .gt('created_at', lastSeen)
          .neq('created_by', user.id)
          .limit(10),
        
        // New files
        supabase
          .from('files')
          .select('id, name')
          .gt('created_at', lastSeen)
          .neq('created_by', user.id)
          .limit(10),
        
        // New bookings (Finanzen)
        supabase
          .from('bookings')
          .select('id, description, amount, type')
          .gt('created_at', lastSeen)
          .neq('created_by', user.id)
          .limit(10),
        
        // New marketing campaigns
        supabase
          .from('marketing_campaigns')
          .select('id, name, campaign_type')
          .gt('created_at', lastSeen)
          .neq('created_by', user.id)
          .limit(10),
        
        // New insurances
        supabase
          .from('project_insurances')
          .select('id, insurance_type, provider')
          .gt('created_at', lastSeen)
          .neq('created_by', user.id)
          .limit(10),
        
        // New contacts
        supabase
          .from('contacts')
          .select('id, first_name, last_name, company_name')
          .gt('created_at', lastSeen)
          .neq('created_by', user.id)
          .limit(10),
      ]);

      const tasks: TaskDetail[] = (tasksRes.data || []).map(t => ({
        id: t.id,
        title: t.title,
        description: t.description,
        project_id: t.project_id,
      }));

      const taskNotes: TaskNoteDetail[] = (taskNotesRes.data || []).map((n: any) => ({
        id: n.id,
        task_id: n.task_id,
        task_title: n.tasks?.title || 'Unbekannte Aufgabe',
        content: n.content,
        created_by: n.created_by,
      }));

      const chatMessages: ChatMessageDetail[] = (chatRes.data || []).map((m: any) => ({
        id: m.id,
        content: m.content,
        sender_name: m.profiles?.full_name || 'Unbekannt',
      }));

      const calendarEvents: CalendarEventDetail[] = (calendarRes.data || []).map(e => ({
        id: e.id,
        title: e.title,
        start_date: e.start_date,
      }));

      const projects = (projectsRes.data || []).map(p => ({
        id: p.id,
        name: p.name,
      }));

      const files = (filesRes.data || []).map(f => ({
        id: f.id,
        name: f.name,
      }));

      const bookings: BookingDetail[] = (bookingsRes.data || []).map(b => ({
        id: b.id,
        description: b.description,
        amount: b.amount,
        type: b.type,
      }));

      const campaigns: CampaignDetail[] = (campaignsRes.data || []).map(c => ({
        id: c.id,
        name: c.name,
        campaign_type: c.campaign_type,
      }));

      const insurances: InsuranceDetail[] = (insurancesRes.data || []).map(i => ({
        id: i.id,
        insurance_type: i.insurance_type,
        provider: i.provider,
      }));

      const contacts: ContactDetail[] = (contactsRes.data || []).map(c => ({
        id: c.id,
        first_name: c.first_name,
        last_name: c.last_name,
        company_name: c.company_name,
      }));

      const newSummary: LoginSummary = {
        tasks,
        taskNotes,
        chatMessages,
        calendarEvents,
        projects,
        files,
        bookings,
        campaigns,
        insurances,
        contacts,
      };

      // Check if there's anything new
      const hasNewItems = 
        tasks.length > 0 ||
        taskNotes.length > 0 ||
        chatMessages.length > 0 ||
        calendarEvents.length > 0 ||
        projects.length > 0 ||
        files.length > 0 ||
        bookings.length > 0 ||
        campaigns.length > 0 ||
        insurances.length > 0 ||
        contacts.length > 0;

      if (hasNewItems) {
        setSummary(newSummary);
        setShouldShow(true);
      }
    } catch (error) {
      console.error('Error fetching login summary:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const markAsSeen = useCallback(() => {
    sessionStorage.setItem(SESSION_KEY, 'true');
    setShouldShow(false);
  }, []);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  return {
    summary,
    loading,
    shouldShow,
    markAsSeen,
  };
};
